import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { userHistoryApi, UserChangeHistory } from '../services/api';

interface UserHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

export const UserHistoryDialog: React.FC<UserHistoryDialogProps> = ({
  open,
  onClose,
  userId,
  userName
}) => {
  const [history, setHistory] = useState<UserChangeHistory[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState('');
  const [summary, setSummary] = useState<any>(null);

  useEffect(() => {
    if (open && userId) {
      fetchHistory();
      fetchSummary();
    }
  }, [open, userId, page, rowsPerPage, actionFilter]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await userHistoryApi.getUserHistory(
        userId,
        page + 1,
        rowsPerPage,
        actionFilter ? { action: actionFilter } : undefined
      );
      setHistory(response.data.history);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching user history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await userHistoryApi.getUserHistorySummary(userId);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getActionColor = (action: string) => {
    const colors: { [key: string]: 'success' | 'error' | 'warning' | 'info' | 'default' } = {
      CREATE: 'success',
      UPDATE: 'info',
      DELETE: 'error',
      PAUSE: 'warning',
      UNPAUSE: 'success',
      PASSWORD_RESET: 'warning',
      ONBOARD: 'success'
    };
    return colors[action] || 'default';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Change History - {userName}
        {summary && (
          <Typography variant="body2" color="textSecondary">
            Total Changes: {summary.totalChanges} | First: {summary.firstChange ? formatDate(summary.firstChange) : 'N/A'} | Last: {summary.lastChange ? formatDate(summary.lastChange) : 'N/A'}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Action</InputLabel>
            <Select
              value={actionFilter}
              label="Filter by Action"
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="CREATE">Create</MenuItem>
              <MenuItem value="UPDATE">Update</MenuItem>
              <MenuItem value="DELETE">Delete</MenuItem>
              <MenuItem value="PAUSE">Pause</MenuItem>
              <MenuItem value="UNPAUSE">Unpause</MenuItem>
              <MenuItem value="PASSWORD_RESET">Password Reset</MenuItem>
              <MenuItem value="ONBOARD">Onboard</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Changed By</TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell>Old Value</TableCell>
                    <TableCell>New Value</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No change history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{formatDate(record.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={record.action}
                            color={getActionColor(record.action)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {record.changed_by_first_name && record.changed_by_last_name
                            ? `${record.changed_by_first_name} ${record.changed_by_last_name}`
                            : record.changed_by_email || 'System'}
                        </TableCell>
                        <TableCell>{record.field_name || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record.old_value ? formatValue(record.old_value) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record.new_value ? formatValue(record.new_value) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record.change_reason || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(record.before_snapshot || record.after_snapshot) && (
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="caption">View Snapshots</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  {record.before_snapshot && (
                                    <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
                                      <Typography variant="subtitle2">Before:</Typography>
                                      <Paper sx={{ p: 1, bgcolor: '#f5f5f5', maxHeight: 200, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                                          {JSON.stringify(record.before_snapshot, null, 2)}
                                        </pre>
                                      </Paper>
                                    </Box>
                                  )}
                                  {record.after_snapshot && (
                                    <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
                                      <Typography variant="subtitle2">After:</Typography>
                                      <Paper sx={{ p: 1, bgcolor: '#f5f5f5', maxHeight: 200, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                                          {JSON.stringify(record.after_snapshot, null, 2)}
                                        </pre>
                                      </Paper>
                                    </Box>
                                  )}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
